import React, { useState } from 'react';
import { FaTiktok } from 'react-icons/fa';
import API_BASE_URL from '../config';
import { motion } from 'framer-motion';
import {
  Mail,
  Send,
  Linkedin,
  Facebook,
  Twitter,
  Youtube,
  Instagram,
  Calendar,
  User,
  Phone,
} from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    date: '',
    time: '',
  });

  const [status, setStatus] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('Sending...');

    try {
      const response = await fetch(`${API_BASE_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (data.success) {
        setStatus('Message sent successfully!');
        setFormData({
          name: '',
          email: '',
          phone: '',
          date: '',
          time: ''
        });
        setTimeout(() => setStatus(''), 3000);
      } else {
        throw new Error(data.error || 'Failed to send');
      }
    } catch (error) {
      setStatus('Failed to send message. Please try again.');
      console.error('Contact submit error:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-yellow-50 pt-32 md:pt-40 pb-20">
      <div className="container mx-auto px-4 max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Get in Touch</h1>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Let's connect and discuss how we can work together. Choose your preferred way to reach out.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-8"
          >
            <div className="bg-white rounded-2xl shadow-xl p-8 hover:shadow-2xl transition">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <Mail className="w-6 h-6 text-yellow-600" />
                Email & Social Media
              </h2>

              <motion.a
                href="mailto:bookamaraoyeka@gmail.com"
                className="block bg-gradient-to-r from-yellow-50 to-white p-4 rounded-xl mb-6 hover:scale-105 transition"
                whileHover={{ y: -5 }}
              >
                <div className="flex items-center gap-4">
                  <div className="bg-yellow-100 p-3 rounded-full">
                    <Send className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div>
                    <div className="font-semibold text-lg">Email Me</div>
                    <div className="text-gray-600">bookamaraoyeka@gmail.com</div>
                  </div>
                </div>
              </motion.a>

              {/* Social Media Links Row */}
              <h3 className="font-semibold text-gray-700 mb-4">Connect on Social Media</h3>

              <div className="flex flex-wrap gap-4 justify-center">
                {/* LinkedIn Link */}
                <motion.a
                  href="https://www.linkedin.com/in/oyeka-amara-kate-b-sc-m-sc-fca-fcti-a9a6a91aa/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center bg-yellow-100 p-4 rounded-full hover:scale-110 transition"
                  whileHover={{ y: -3 }}
                >
                  <Linkedin className="w-6 h-6 text-yellow-600" />
                </motion.a>

                {/* Facebook Link */}
                <motion.a
                  href="https://www.facebook.com/ama.kate.3110"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center bg-yellow-100 p-4 rounded-full hover:scale-110 transition"
                  whileHover={{ y: -3 }}
                >
                  <Facebook className="w-6 h-6 text-yellow-600" />
                </motion.a>

                {/* Twitter Link */}
                <motion.a
                  href="https://x.com/amacecekate"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center bg-yellow-100 p-4 rounded-full hover:scale-110 transition"
                  whileHover={{ y: -3 }}
                >
                  <Twitter className="w-6 h-6 text-yellow-600" />
                </motion.a>

                {/* Instagram Link */}
                <motion.a
                  href="https://www.instagram.com/officialkate_kenstar/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center bg-yellow-100 p-4 rounded-full hover:scale-110 transition"
                  whileHover={{ y: -3 }}
                >
                  <Instagram className="w-6 h-6 text-yellow-600" />
                </motion.a>

                {/* Youtube Link */}
                <motion.a
                  href="https://www.youtube.com/@Thegreenmicrophone-byAK"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center bg-yellow-100 p-4 rounded-full hover:scale-110 transition"
                  whileHover={{ y: -3 }}
                >
                  <Youtube className="w-6 h-6 text-yellow-600" />
                </motion.a>
                
                {/* TikTok Link */}
                <motion.a
                  href="https://www.tiktok.com/@officialkate_kenstar"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center bg-yellow-100 p-4 rounded-full hover:scale-110 transition"
                  whileHover={{ y: -3 }}
                >
                  <FaTiktok className="w-6 h-6 text-yellow-600" />
                </motion.a>
              </div>

            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
          >
            <div className="bg-white rounded-2xl shadow-xl p-8 hover:shadow-2xl transition">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-3">
                <Calendar className="w-6 h-6 text-yellow-600" />
                Book a Consultation
              </h2>

              {status && (
                <p className={`text-center text-lg mb-4 ${status.includes('successfully') ? 'text-green-500' : 'text-red-500'}`}>
                  {status}
                </p>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Your Name</label>
                  <div className="relative">
                    <User className="absolute left-3 top-3 text-gray-400" />
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-4 py-2 border rounded-lg"
                      placeholder="John Doe"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 text-gray-400" />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-4 py-2 border rounded-lg"
                        placeholder="your@email.com"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Phone</label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-3 text-gray-400" />
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-4 py-2 border rounded-lg"
                        placeholder="+234 0100 000 000"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Preferred Date</label>
                    <input
                      type="date"
                      name="date"
                      value={formData.date}
                      onChange={handleInputChange}
                      className="w-full pl-3 pr-4 py-2 border rounded-lg"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Preferred Time</label>
                    <input
                      type="time"
                      name="time"
                      value={formData.time}
                      onChange={handleInputChange}
                      className="w-full pl-3 pr-4 py-2 border rounded-lg"
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full bg-yellow-600 text-white py-3 rounded-lg hover:bg-yellow-700 transition"
                >
                  Send Message
                </button>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Contact;